import React, { useState, useEffect, useReducer, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Tooltip,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RoomServiceIcon from "@material-ui/icons/RoomService";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import LinkIcon from "@material-ui/icons/Link";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import ServiceModal from "../../components/ServiceModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import { financeiroStyles, KpiStrip, toneColor } from "../Financeiro/financeiroShared";
import ServiceDeleteDialog from "./ServiceDeleteDialog";
import BookingPageSettings from "./BookingPageSettings";

const useStyles = makeStyles((theme) => ({
  ...financeiroStyles(theme),
  // a página é uma coluna flex: estes blocos não podem encolher
  header: { ...financeiroStyles(theme).header, flexShrink: 0 },
  tabs: { ...financeiroStyles(theme).tabs, flexShrink: 0 },
  kpis: { ...financeiroStyles(theme).kpis, flexShrink: 0 },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingBottom: theme.spacing(2),
  },
  linkBar: {
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.75),
    color: theme.palette.text.primary,
  },
  linkLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  linkUrl: {
    flex: "1 1 220px",
    minWidth: 0,
    fontWeight: 500,
    color: theme.palette.primary.main,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  smallButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    height: 32,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
  serviceName: { fontWeight: 500 },
  avatars: { display: "flex", alignItems: "center" },
  avatar: {
    width: 28,
    height: 28,
    fontSize: 11,
    fontWeight: 700,
    border: `2px solid ${theme.palette.background.paper}`,
    "& + &": { marginLeft: -8 },
  },
  avatarNames: { marginLeft: 8, fontSize: 13, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  inactiveRow: { "& > td:not(:last-child)": { opacity: 0.55 } },
  num: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  pillBlue: {
    color: toneColor(theme, "blue"),
    backgroundColor: theme.palette.type === "dark" ? "rgba(100,181,246,.16)" : "#e3f0fd",
  },
}));

const AVATAR_COLORS = ["#2D7BE8", "#7b61ff", "#2e9d5b", "#f57c00", "#d81b60", "#00897b", "#5c6bc0", "#8d6e63"];
const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

export const formatPrice = (value) =>
  Number(value || 0) > 0
    ? Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "Grátis";

export const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h${String(rest).padStart(2, "0")}` : `${h} h`;
};

// Se o serviço aparece na página pública e, se não, por quê.
const visibility = (service) => {
  if (!service.active) return "off";
  const hasProvider = (service.users || []).some((u) => u.isServiceProvider);
  return hasProvider ? "visible" : "noProvider";
};

const VISIBILITY = {
  visible: { label: "Visível", cls: "pillGreen" },
  noProvider: { label: "Oculto · sem profissional", cls: "pillAmber" },
  off: { label: "Desativado", cls: "pillGrey" },
};

const FILTERS = [
  ["all", "Todos"],
  ["visible", "Na página"],
  ["noProvider", "Sem profissional"],
  ["off", "Desativados"],
];

const reducer = (state, action) => {
  if (action.type === "LOAD_SERVICES") {
    const services = action.payload;
    const byId = new Map(services.map((s) => [s.id, s]));
    const merged = state.map((s) => byId.get(s.id) || s);
    const newServices = services.filter((s) => !state.some((old) => old.id === s.id));
    return [...merged, ...newServices];
  }
  if (action.type === "UPDATE_SERVICE") {
    const service = action.payload;
    const old = state.find((s) => s.id === service.id);
    if (old) {
      // o evento do socket não traz a contagem dos últimos 30 dias
      const next = {
        ...old,
        ...service,
        appointmentsLast30: service.appointmentsLast30 ?? old.appointmentsLast30,
      };
      return state.map((s) => (s.id === service.id ? next : s));
    }
    return [...state, service].sort((a, b) => a.name.localeCompare(b.name));
  }
  if (action.type === "DELETE_SERVICE") {
    return state.filter((s) => s.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const Services = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filter, setFilter] = useState("all");
  const [services, dispatch] = useReducer(reducer, []);
  const [summary, setSummary] = useState(null);

  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, service: null });

  const [slug, setSlug] = useState("");

  const fetchSlug = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${user.companyId}/slug`);
      setSlug(data.slug || "");
    } catch (err) {
      // sem acesso: a barra do link fica oculta
    }
  }, [user.companyId]);

  useEffect(() => {
    if (user?.companyId) fetchSlug();
  }, [user, fetchSlug]);

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await api.get("/services", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_SERVICES", payload: data.services });
      setHasMore(data.hasMore);
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [searchParam, pageNumber]);

  // Os números do topo valem para todos os serviços, não só os carregados.
  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/services", { params: { pageNumber: 1 } });
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      // silencioso: os números atualizam na próxima carga
    }
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => {
      fetchServices();
    }, 400);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, fetchServices]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    socket.on("service", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SERVICE", payload: data.service });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SERVICE", payload: +data.serviceId });
      }
      refreshSummary();
    });
    return () => {
      socket.disconnect();
    };
  }, [user, refreshSummary]);

  const publicUrl = slug ? `${window.location.origin}/agenda/${slug}` : "";

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado!");
    } catch (err) {
      toast.info(publicUrl);
    }
  };

  const openNew = () => {
    setSelectedServiceId(null);
    setServiceModalOpen(true);
  };

  const openEdit = (service) => {
    setSelectedServiceId(service.id);
    setServiceModalOpen(true);
  };

  const handleServiceSaved = (saved) => {
    if (saved && saved.id) dispatch({ type: "UPDATE_SERVICE", payload: saved });
    refreshSummary();
  };

  const closeMenu = () => setMenu({ anchor: null, service: null });

  const handleToggleActive = async (service) => {
    closeMenu();
    try {
      const { data } = await api.put(`/services/${service.id}`, { active: !service.active });
      dispatch({ type: "UPDATE_SERVICE", payload: data });
      toast.success(data.active ? "Serviço reativado." : "Serviço desativado.");
      refreshSummary();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicate = async (service) => {
    closeMenu();
    try {
      const { data } = await api.post("/services", {
        name: `${service.name} (cópia)`.slice(0, 80),
        durationMinutes: service.durationMinutes,
        price: Number(service.price) || 0,
        active: false,
        userIds: (service.users || []).map((u) => u.id),
      });
      dispatch({ type: "UPDATE_SERVICE", payload: data });
      toast.success("Cópia criada como desativada. Revise e ative quando quiser.");
      refreshSummary();
      openEdit(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleted = (serviceId) => {
    dispatch({ type: "DELETE_SERVICE", payload: serviceId });
    refreshSummary();
  };

  const handleDeactivatedFromDialog = (saved) => {
    dispatch({ type: "UPDATE_SERVICE", payload: saved });
    refreshSummary();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const visibleRows = useMemo(
    () => services.filter((s) => filter === "all" || visibility(s) === filter),
    [services, filter]
  );

  const filterCount = (key) => {
    if (!summary) return services.filter((s) => key === "all" || visibility(s) === key).length;
    if (key === "all") return summary.total;
    if (key === "visible") return summary.visible;
    if (key === "noProvider") return summary.noProvider;
    return summary.inactive;
  };

  const subtitle = summary
    ? `${summary.total} ${summary.total === 1 ? "serviço" : "serviços"} · ${summary.visible} ${
        summary.visible === 1 ? "disponível" : "disponíveis"
      } no link`
    : "";

  const kpis = [
    {
      label: "Serviços",
      value: summary ? summary.total : "—",
      caption: summary
        ? summary.inactive
          ? `${summary.inactive} ${summary.inactive === 1 ? "desativado" : "desativados"}`
          : "todos ativos"
        : "",
      tone: "grey",
    },
    {
      label: "Na página pública",
      value: summary ? summary.visible : "—",
      caption: "o cliente consegue agendar",
      tone: "green",
    },
    {
      label: "Sem profissional",
      value: summary ? summary.noProvider : "—",
      caption: "ativos, mas ocultos no link",
      tone: "amber",
      colorValue: Boolean(summary && summary.noProvider),
    },
    {
      label: "Agendados (30 dias)",
      value: summary ? summary.appointmentsLast30 : "—",
      caption: "todos os serviços",
      tone: "blue",
    },
  ];

  const renderProviders = (service) => {
    const providers = (service.users || []).filter((u) => u.isServiceProvider !== false);
    if (providers.length === 0) {
      return <Chip size="small" label="Nenhum" className={`${classes.pill} ${classes.pillAmber}`} />;
    }
    return (
      <div className={classes.avatars}>
        {providers.slice(0, 4).map((u) => (
          <Tooltip key={u.id} title={u.name}>
            <Avatar className={classes.avatar} style={{ backgroundColor: avatarColor(u.id), color: "#fff" }}>
              {initials(u.name)}
            </Avatar>
          </Tooltip>
        ))}
        <span className={classes.avatarNames}>
          {providers.length === 1 ? providers[0].name : `${providers.length} profissionais`}
        </span>
      </div>
    );
  };

  const renderVisibility = (service) => {
    const cfg = VISIBILITY[visibility(service)];
    return <Chip size="small" label={cfg.label} className={`${classes.pill} ${classes[cfg.cls]}`} />;
  };

  const renderActions = (service) => (
    <div className={classes.actions}>
      {!isMobile && (
        <Tooltip title="Editar">
          <IconButton size="small" className={classes.iconAction} onClick={() => openEdit(service)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Mais ações">
        <IconButton
          size="small"
          className={classes.iconAction}
          aria-label="Mais ações"
          onClick={(e) => setMenu({ anchor: e.currentTarget, service })}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const emptyText = searchParam
    ? `Nenhum serviço encontrado para "${searchParam}".`
    : filter !== "all"
    ? "Nenhum serviço neste filtro."
    : "Nenhum serviço cadastrado ainda.";

  return (
    <MainContainer>
      <ServiceModal
        open={serviceModalOpen}
        onClose={() => {
          setServiceModalOpen(false);
          setSelectedServiceId(null);
        }}
        reload={handleServiceSaved}
        serviceId={selectedServiceId}
      />
      <ServiceDeleteDialog
        service={deletingService}
        onClose={() => setDeletingService(null)}
        onDeleted={handleDeleted}
        onDeactivated={handleDeactivatedFromDialog}
      />
      <Menu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            const s = menu.service;
            closeMenu();
            openEdit(s);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Editar" />
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(menu.service)}>
          <ListItemIcon>
            <FileCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicar" />
        </MenuItem>
        <MenuItem onClick={() => handleToggleActive(menu.service)}>
          <ListItemIcon>
            {menu.service && menu.service.active ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText primary={menu.service && menu.service.active ? "Desativar" : "Reativar"} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const s = menu.service;
            closeMenu();
            setDeletingService(s);
          }}
          style={{ color: theme.palette.error.main }}
        >
          <ListItemIcon style={{ color: "inherit" }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Excluir" />
        </MenuItem>
      </Menu>

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Serviços</Title>
          {subtitle && (
            <Typography variant="body2" className={classes.muted}>
              {subtitle}
            </Typography>
          )}
        </div>
        {tab === 0 && (
          <div className={classes.headerButtons}>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              {isMobile ? "Novo" : "Novo serviço"}
            </Button>
          </div>
        )}
      </div>

      <div className={classes.tabs} role="tablist" aria-label="Seções de serviços">
        {["Serviços", "Página pública"].map((label, i) => (
          <Button
            key={label}
            role="tab"
            aria-selected={tab === i}
            className={`${classes.tab} ${tab === i ? classes.tabActive : ""}`}
            onClick={() => setTab(i)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === 1 && (
        <div className={classes.scrollArea}>
          <BookingPageSettings onSlugSaved={setSlug} />
        </div>
      )}

      {tab === 0 && (
        <>
          <div className={classes.linkBar}>
            <LinkIcon style={{ color: theme.palette.text.secondary }} fontSize="small" />
            {publicUrl ? (
              <>
                {!isMobile && <span className={classes.linkLabel}>Link de agendamento</span>}
                <span className={classes.linkUrl}>{publicUrl.replace(/^https?:\/\//, "")}</span>
                <Button variant="outlined" size="small" className={classes.smallButton} onClick={handleCopyUrl}>
                  Copiar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.smallButton}
                  endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver como cliente
                </Button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, minWidth: 0 }}>
                  Seus clientes ainda não podem agendar sozinhos. Crie o link da sua página.
                </span>
                <Button variant="outlined" size="small" className={classes.smallButton} onClick={() => setTab(1)}>
                  Criar link
                </Button>
              </>
            )}
          </div>

          <KpiStrip items={kpis} classes={classes} theme={theme} loading={!summary} />

          <Paper className={classes.mainPaper} variant="outlined">
            <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
              <TextField
                className={classes.searchField}
                placeholder="Buscar serviço"
                type="search"
                variant="outlined"
                size="small"
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                inputProps={{ "aria-label": "Buscar serviço" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
              <div
                className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`}
                role="group"
                aria-label="Filtrar serviços"
              >
                {FILTERS.map(([key, label]) => (
                  <Chip
                    key={key}
                    clickable
                    className={classes.filterChip}
                    label={`${label} (${filterCount(key)})`}
                    color={filter === key ? "primary" : "default"}
                    variant={filter === key ? "default" : "outlined"}
                    aria-pressed={filter === key}
                    onClick={() => setFilter(key)}
                  />
                ))}
              </div>
            </div>

            <div className={classes.tableWrapper} onScroll={handleScroll}>
              {!loading && visibleRows.length === 0 && (
                <div className={classes.emptyState}>
                  <RoomServiceIcon className={classes.emptyIcon} />
                  <Typography variant="body2">{emptyText}</Typography>
                  {!searchParam && filter === "all" && (
                    <Button size="small" color="primary" variant="outlined" onClick={openNew}>
                      Criar primeiro serviço
                    </Button>
                  )}
                </div>
              )}

              {isMobile ? (
                <div>
                  {visibleRows.map((service) => (
                    <div
                      key={service.id}
                      className={`${classes.mobileRow} ${service.active ? "" : classes.inactiveRow}`}
                      onClick={() => openEdit(service)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={classes.mobileInfo}>
                        <div className={classes.mobileTitle}>{service.name}</div>
                        <div className={classes.subtle}>
                          {[
                            formatDuration(service.durationMinutes),
                            formatPrice(service.price),
                            (service.users || []).map((u) => u.name.split(" ")[0]).join(", "),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        <div style={{ marginTop: 6 }}>{renderVisibility(service)}</div>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>{renderActions(service)}</div>
                    </div>
                  ))}
                  {loading && (
                    <Typography align="center" className={classes.muted} style={{ padding: 24 }}>
                      Carregando...
                    </Typography>
                  )}
                </div>
              ) : (
                (visibleRows.length > 0 || loading) && (
                  <Table stickyHeader size="small" aria-label="serviços" style={{ minWidth: 860 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>
                          Serviço
                        </TableCell>
                        <TableCell className={classes.headCell}>Duração</TableCell>
                        <TableCell className={classes.headCell}>Preço</TableCell>
                        <TableCell className={classes.headCell}>Profissionais</TableCell>
                        <TableCell className={classes.headCell}>Na página pública</TableCell>
                        <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRows.map((service) => (
                        <TableRow
                          key={service.id}
                          className={`${classes.row} ${service.active ? "" : classes.inactiveRow}`}
                        >
                          <TableCell style={{ paddingLeft: 20 }}>
                            <div className={classes.serviceName}>{service.name}</div>
                            <div className={classes.subtle}>
                              {service.appointmentsLast30
                                ? `${service.appointmentsLast30} ${
                                    service.appointmentsLast30 === 1 ? "agendamento" : "agendamentos"
                                  } em 30 dias`
                                : "Nenhum agendamento em 30 dias"}
                            </div>
                          </TableCell>
                          <TableCell className={classes.num}>{formatDuration(service.durationMinutes)}</TableCell>
                          <TableCell className={classes.num}>{formatPrice(service.price)}</TableCell>
                          <TableCell>{renderProviders(service)}</TableCell>
                          <TableCell>{renderVisibility(service)}</TableCell>
                          <TableCell align="right" style={{ paddingRight: 12 }}>
                            {renderActions(service)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" className={classes.muted}>
                            Carregando...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )
              )}
            </div>
            <div className={classes.footer}>
              {summary
                ? `Mostrando ${visibleRows.length} de ${filterCount(filter)} · ordenado por nome`
                : "Ordenado por nome"}
            </div>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Services;
